/**
 * Business dashboard session APIs.
 */
import {
  fetchFetchedalllearninghub,
  fetchFetchedallplanswithcurrentplanById,
  fetchFetchagentById,
  fetchGetInteractedCustomerdetailsCountByPhoneNoId,
  fetchProjectsById,
  postFetchlatestcampaign,
  postFetchtotalsentmessagetype,
  postUpdateCustomerStatus,
} from "./customer.api";
import { apihubGet, apihubPost } from "./apihub";
import { fetchTotalRemainingQuota } from "./quota";

const statsPayload = (phoneNoId, filterType = "today") => ({
  phonenoId: phoneNoId,
  filterType,
});

/**
 * Dashboard widgets are independent, so a 404 from one endpoint (empty table)
 * must not blank out the whole page.
 */
const settle = (promise) => Promise.resolve(promise).then((v) => v, () => null);

export function updateAgentStatus(userId, extractedalphaemail) {
  return postUpdateCustomerStatus({ userId, extractedalphaemail });
}

export function fetchPlansWithCurrent(projectId) {
  return fetchFetchedallplanswithcurrentplanById({ id: projectId });
}

const PROJECT_CACHE_TTL_MS = 30000;
const projectCache = new Map();

export function fetchProject(projectId) {
  const id = String(projectId || "").trim();
  if (!id) return Promise.resolve(null);
  const hit = projectCache.get(id);
  if (hit && Date.now() - hit.at < PROJECT_CACHE_TTL_MS) {
    return hit.promise;
  }
  const promise = fetchProjectsById({ id }).catch((err) => {
    projectCache.delete(id);
    throw err;
  });
  projectCache.set(id, { at: Date.now(), promise });
  return promise;
}

export function fetchAgentPreferences(projectId) {
  return fetchFetchagentById({ id: projectId });
}

export function fetchChatCounts(phoneNoId) {
  return fetchGetInteractedCustomerdetailsCountByPhoneNoId({ phoneNoId });
}

export function fetchRemainingQuota(phoneNoId) {
  return fetchTotalRemainingQuota(phoneNoId);
}

export function invalidateProjectCache(projectId) {
  const id = String(projectId || "").trim();
  if (id) projectCache.delete(id);
}

export async function syncWabaAccount(projectId) {
  const id = String(projectId || "").trim();
  if (!id) {
    return { success: false, message: "projectId is required" };
  }
  try {
    const res = await apihubPost("/getInteractedCustomerDetail/syncwhatsapp", {
      projectId: id,
    });
    invalidateProjectCache(id);
    return res;
  } catch (err) {
    return {
      success: false,
      message: err.message || "Failed to sync WhatsApp account",
    };
  }
}

export async function fetchMetaPhoneStatus(phoneNoId) {
  const phonenoId = phoneNoId != null ? String(phoneNoId).trim() : "";
  if (!phonenoId) {
    return { success: false, message: "phonenoId is required" };
  }
  try {
    return await apihubGet(
      `/getInteractedCustomerDetail/metaphonestatus/${encodeURIComponent(phonenoId)}`,
    );
  } catch (err) {
    return {
      success: false,
      message: err.message || "Failed to fetch Meta phone status",
    };
  }
}

export function fetchLatestCampaignSummary(phoneNoId, filterType = "today") {
  return postFetchlatestcampaign(statsPayload(phoneNoId, filterType));
}

export function fetchSentMessagesByType(phoneNoId, filterType = "today") {
  return postFetchtotalsentmessagetype(statsPayload(phoneNoId, filterType));
}

export async function loadDashboardSession({
  projectId,
  userId,
  userEmail,
}) {
  if (userId && userEmail) {
    updateAgentStatus(userId, userEmail).catch(() => null);
  }

  const [projectRes, plansRes, learningRes] = await Promise.all([
    settle(fetchProject(projectId)),
    settle(fetchPlansWithCurrent(projectId)),
    settle(fetchFetchedalllearninghub()),
  ]);

  const project = projectRes?.project || projectRes?.data || projectRes;
  const resolvedPhone =
    project?.phone_number_id != null && String(project.phone_number_id).trim()
      ? String(project.phone_number_id).trim()
      : "";

  let quota = null;
  let chatCounts = null;
  if (resolvedPhone) {
    [quota, chatCounts] = await Promise.all([
      settle(fetchRemainingQuota(resolvedPhone)),
      settle(fetchChatCounts(resolvedPhone)),
    ]);
  }

  return {
    shell: [null, null, null, null, learningRes, null, plansRes, projectRes],
    project,
    phoneNoId: resolvedPhone || null,
    quota,
    chatCounts,
    latestCampaign: null,
    messageTypes: null,
    metaStatus: null,
  };
}
